import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Reveal, Tag, Heading, EASE } from "./Reveal";

const SLIDES = [
  { src: "/images/ba-1.png", title: "Builder-grade oak to warm white shaker", meta: "14 cabinets · 3 days on site · $5K guaranteed" },
  { src: "/images/ba-2.png", title: "Dated bath & kitchen, both revived", meta: "5 days on site · cabinets, tile & vanity" },
  { src: "/images/ba-3.png", title: "Dark oak island kitchen, opened up", meta: "18 cabinets · 4 days on site · $5K guaranteed" },
  { src: "/images/ba-4.png", title: "Heavy walnut to crisp, bright white", meta: "12 cabinets · 3 days on site · $5K guaranteed" },
];

const EXTRAS = ["Add a backsplash", "Replace the flooring", "Add an island", "Change the lighting", "Reconfigure the space"];

const NavBtn = ({ onClick, testId, label, children }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    aria-label={label}
    className="h-14 w-14 border border-ink/20 flex items-center justify-center text-ink hover:bg-ink hover:text-bone hover:border-ink transition-colors duration-300"
  >
    {children}
  </button>
);

const Slideshow = () => {
  const [[index, direction], setIndex] = useState([0, 0]);
  const go = (dir) => setIndex(([i]) => [(i + dir + SLIDES.length) % SLIDES.length, dir]);
  const slide = SLIDES[index];

  return (
    <div data-testid="beforeafter-slideshow">
      <div className="relative overflow-hidden bg-sand">
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.img
            key={slide.src}
            src={slide.src}
            alt={slide.title}
            data-testid="beforeafter-slide-image"
            className="w-full aspect-[3/2] lg:aspect-[21/10] object-cover"
            initial={{ opacity: 0, x: direction >= 0 ? 70 : -70, scale: 1.02 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: direction >= 0 ? -70 : 70 }}
            transition={{ duration: 0.55, ease: EASE }}
          />
        </AnimatePresence>
      </div>
      <div className="mt-6 grid md:grid-cols-12 gap-6 items-start">
        <div className="md:col-span-2 flex items-baseline gap-2">
          <span className="font-display font-semibold text-5xl sm:text-6xl tracking-[-0.03em] text-ink leading-none" data-testid="slideshow-counter">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="font-mono text-xs text-ink/40">/ {String(SLIDES.length).padStart(2, "0")}</span>
        </div>
        <div className="md:col-span-6">
          <p className="font-display font-medium text-2xl sm:text-3xl tracking-[-0.015em] text-ink leading-tight" data-testid="slideshow-caption">{slide.title}</p>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.14em] text-ink/50">{slide.meta}</p>
        </div>
        <div className="md:col-span-4 flex md:justify-end gap-2">
          <NavBtn onClick={() => go(-1)} testId="slideshow-prev-btn" label="Previous transformation"><ArrowLeft className="h-5 w-5" /></NavBtn>
          <NavBtn onClick={() => go(1)} testId="slideshow-next-btn" label="Next transformation"><ArrowRight className="h-5 w-5" /></NavBtn>
        </div>
      </div>
      <div className="mt-8 grid grid-cols-4 gap-2">
        {SLIDES.map((s, i) => (
          <button
            key={s.src}
            onClick={() => setIndex([i, i > index ? 1 : -1])}
            data-testid={`slideshow-thumb-${i}`}
            aria-label={`Show transformation ${i + 1}`}
            className={`relative aspect-[3/2] overflow-hidden transition-opacity duration-300 ${i === index ? "opacity-100" : "opacity-40 hover:opacity-80"}`}
          >
            <img src={s.src} alt="" className="h-full w-full object-cover" />
            {i === index && <span className="absolute inset-x-0 bottom-0 h-1 bg-teal" />}
          </button>
        ))}
      </div>
    </div>
  );
};

export const Transformation = () => (
  <section id="transformations" className="py-24 lg:py-40" data-testid="transformations-section">
    <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
        <Reveal className="lg:col-span-5">
          <Heading className="text-6xl sm:text-8xl lg:text-9xl">Yes,<br />really.</Heading>
        </Reveal>
        <Reveal className="lg:col-span-6 lg:col-start-7 lg:pt-4" delay={0.1}>
          <p className="text-lg sm:text-xl text-ink/70 leading-relaxed">
            Construction Alliance has access to beautiful, quality cabinetry and granite at exceptional pricing — and instead of marking it up into oblivion, we pass that value on to you. That means the tired, dated kitchen you've been living with could look completely different without a $30,000 remodel or months of construction.
          </p>
          <p className="mt-6 font-display font-medium text-xl sm:text-2xl tracking-[-0.015em] text-ink leading-snug">
            Beautiful cabinets. Granite countertops. Expert installation. Thoughtful craftsmanship. A whole new feeling when you walk into your kitchen.
          </p>
        </Reveal>
      </div>

      <div className="mt-28 lg:mt-40 grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
        <Reveal className="lg:col-span-7">
          <Tag>What could a few days change?</Tag>
          <Heading className="mt-6">More than you think.</Heading>
        </Reveal>
        <Reveal className="lg:col-span-5" delay={0.1}>
          <p className="text-base sm:text-lg text-ink/70 leading-relaxed">
            You don't always need to tear your entire kitchen down to the studs to transform it. Cabinetry takes up an enormous amount of visual real estate. Change the cabinets, and suddenly the entire room feels different.
          </p>
          <p className="mt-4 font-display font-medium text-xl text-ink">Fresh. Current. Clean. Beautiful.</p>
        </Reveal>
      </div>

      <Reveal className="mt-14" delay={0.1}>
        <Slideshow />
      </Reveal>

      <Reveal className="mt-16 flex flex-col md:flex-row md:items-center gap-6 border-t border-ink/10 pt-8">
        <p className="font-display font-medium text-lg text-ink shrink-0">Want to take it further?</p>
        <div className="flex flex-wrap gap-2">
          {EXTRAS.map((e) => (
            <span key={e} className="font-sans font-medium text-sm text-ink border border-ink/20 px-4 py-2 hover:border-teal hover:text-teal transition-colors duration-300 cursor-default">
              {e}
            </span>
          ))}
        </div>
        <p className="text-sm text-ink/50 md:ml-auto">Start with what you need. Build from there.</p>
      </Reveal>
    </div>
  </section>
);
